import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/design-system';
import { navigate } from '@/app/router';
import * as dashboardApi from '@/features/dashboard/api/dashboard-api';
import { normalizeEditorGraph } from '@/features/funnel-editor/domain/normalize-graph';
import type { EditorGraph } from '@/features/funnel-editor/domain/editor-graph';
import {
  readPresentationExtension,
  toPresentationGraphInput,
} from '@/features/funnel-editor/presentation/graph-bridge';
import {
  readSimulationExtension,
  toSimulationGraphInput,
} from '@/features/funnel-editor/simulation/graph-bridge';
import { applyScenario } from '@/domain/simulation/scenarios/apply-scenario';
import { runMetricsEngine } from '@/domain/simulation/selectors';
import { normalizePresentationConfig } from '@/domain/presentation/normalize';
import { computeGraphEmphasis } from '@/domain/presentation/emphasis';
import { nextHighlightId, previousHighlightId } from '@/domain/presentation/highlight-navigation';
import { presentationLogoUrl } from './api/presentation-asset-api';
import { usePresentationFullscreen } from './use-presentation-fullscreen';
import { PresentationCover } from './PresentationCover';
import { PresentationHeader, type ScenarioOption } from './PresentationHeader';
import { PresentationCanvas } from './PresentationCanvas';
import { StrategicNotesPanel } from './StrategicNotesPanel';
import { PresentationMetricsPanel } from './PresentationMetricsPanel';
import type { NodeCategory } from '@/domain/funnel/node-system';
import type { PresentationTheme } from '@/domain/presentation/contracts';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; graph: EditorGraph; funnelName: string; funnelId: string };

/**
 * SPEC-011 — PRES-PRINCIPLE-001/003: a read-only view over the latest
 * persisted funnel (never unsaved editor memory, never a second funnel).
 * Deliberately does not mount any editor mutation system (palette,
 * properties panel, connection handlers, autosave coordinator — plan §12).
 */
export function PresentationPage({ id }: { id: string }) {
  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // §9/AC-071 — always the latest persisted graph, fetched fresh here.
        const { funnel } = await dashboardApi.getFunnel(id);
        if (!active) return;
        setLoadState({
          status: 'ready',
          graph: normalizeEditorGraph(funnel.graph),
          funnelName: funnel.name,
          funnelId: id,
        });
      } catch (error) {
        if (active) {
          setLoadState({
            status: 'error',
            message: error instanceof Error ? error.message : 'This funnel could not be opened.',
          });
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  if (loadState.status === 'loading') {
    return (
      <main
        role="status"
        className="flex h-screen items-center justify-center text-sm text-text-muted"
      >
        Loading presentation…
      </main>
    );
  }
  if (loadState.status === 'error') {
    return (
      <main
        role="alert"
        className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center"
      >
        <h1 className="text-xl font-semibold text-text-primary">
          Unable to open this presentation
        </h1>
        <p className="max-w-md text-sm text-text-secondary">{loadState.message}</p>
        <Button variant="secondary" onClick={() => navigate('/app')}>
          Back to dashboard
        </Button>
      </main>
    );
  }

  return (
    <PresentationView
      graph={loadState.graph}
      funnelName={loadState.funnelName}
      funnelId={loadState.funnelId}
    />
  );
}

export function PresentationView({
  graph,
  funnelName,
  funnelId,
  publicView = false,
  publicToken,
}: {
  graph: EditorGraph;
  funnelName: string;
  funnelId: string;
  publicView?: boolean;
  publicToken?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const fullscreen = usePresentationFullscreen(containerRef);

  const rawConfig = readPresentationExtension(graph);
  const graphInput = useMemo(() => toPresentationGraphInput(graph), [graph]);
  const { config } = useMemo(
    () => normalizePresentationConfig(rawConfig, graphInput),
    [rawConfig, graphInput],
  );
  const simulation = readSimulationExtension(graph);

  const [showCover, setShowCover] = useState(config.coverEnabled);
  const [theme, setTheme] = useState<PresentationTheme>(config.theme); // §78 decision — session override, does not persist
  const [scenarioId, setScenarioId] = useState<string | null>(null); // §62 decision — session-local only
  const [activeHighlightId, setActiveHighlightId] = useState<string | null>(null);
  const [stageFocusCategory, setStageFocusCategory] = useState<NodeCategory | null>(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [focusInteracted, setFocusInteracted] = useState(false);

  const activeHighlight = config.highlights.find((h) => h.id === activeHighlightId) ?? null;
  const emphasis = useMemo(
    () =>
      computeGraphEmphasis(graphInput, {
        highlight: activeHighlight
          ? { nodeIds: activeHighlight.nodeIds, edgeIds: activeHighlight.edgeIds }
          : undefined,
        stageFocusCategory: stageFocusCategory ?? undefined,
      }),
    [graphInput, activeHighlight, stageFocusCategory],
  );
  const categoriesPresent = useMemo(
    () => [...new Set(graphInput.nodes.map((n) => n.category))] as NodeCategory[],
    [graphInput],
  );

  const scenarioOptions: ScenarioOption[] = publicView
    ? [{ id: null, label: 'Base' }]
    : [
        { id: null, label: 'Base' },
        ...simulation.scenarios.map((s) => ({ id: s.id, label: s.name })),
      ];
  const metricsResult = useMemo(() => {
    if (!config.showMetrics) return null;
    const simInput = toSimulationGraphInput(graph);
    const scenario = simulation.scenarios.find((s) => s.id === scenarioId) ?? null;
    return runMetricsEngine(applyScenario(simInput, scenario));
  }, [config.showMetrics, graph, simulation.scenarios, scenarioId]);

  function goOverview() {
    setFocusInteracted(true);
    setActiveHighlightId(null);
    setStageFocusCategory(null);
  }
  function goToHighlight(id: string | null) {
    setFocusInteracted(true);
    setActiveHighlightId(id);
    setStageFocusCategory(null);
    const highlight = config.highlights.find((h) => h.id === id);
    if (highlight?.noteId) setNotesOpen(true);
  }

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (event.key === 'ArrowRight') goToHighlight(nextHighlightId(config, activeHighlightId));
      else if (event.key === 'ArrowLeft')
        goToHighlight(previousHighlightId(config, activeHighlightId));
      else if (event.key === '0' || event.key === 'Home') goOverview();
      else if (event.key === 'f' && (event.metaKey || event.ctrlKey) && event.shiftKey)
        void fullscreen.toggle();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, activeHighlightId, fullscreen]);

  const title = config.title || funnelName;
  const logoUrl = config.companyLogoAssetId
    ? publicView && publicToken
      ? `/api/shared/${encodeURIComponent(publicToken)}/assets/${encodeURIComponent(config.companyLogoAssetId)}`
      : presentationLogoUrl(funnelId, config.companyLogoAssetId)
    : undefined;

  if (showCover) {
    return (
      <div ref={containerRef} data-presentation-theme={theme} className="h-screen bg-bg-app">
        <PresentationCover
          title={title}
          subtitle={config.subtitle}
          companyName={config.companyName}
          logoUrl={logoUrl}
          brandingMode={config.brandingMode}
          onStart={() => setShowCover(false)}
        />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      data-presentation-theme={theme}
      className="flex h-screen flex-col bg-bg-app"
    >
      <PresentationHeader
        title={title}
        brandingMode={config.brandingMode}
        companyName={config.companyName}
        logoUrl={logoUrl}
        scenarioOptions={scenarioOptions}
        scenarioId={scenarioId}
        onScenarioChange={setScenarioId}
        categories={categoriesPresent}
        stageFocusCategory={stageFocusCategory}
        onStageFocusChange={(category) => {
          setFocusInteracted(true);
          setStageFocusCategory(category);
          setActiveHighlightId(null);
        }}
        activeHighlight={activeHighlight}
        hasHighlights={config.highlights.some((h) => h.nodeIds.length > 0 || h.edgeIds.length > 0)}
        onPreviousHighlight={() => goToHighlight(previousHighlightId(config, activeHighlightId))}
        onNextHighlight={() => goToHighlight(nextHighlightId(config, activeHighlightId))}
        onOverview={goOverview}
        notesOpen={notesOpen}
        onToggleNotes={publicView ? undefined : () => setNotesOpen((value) => !value)}
        showNotesControl={!publicView}
        theme={theme}
        onToggleTheme={() => setTheme((value) => (value === 'dark' ? 'light' : 'dark'))}
        fullscreenSupported={fullscreen.supported}
        fullscreenActive={fullscreen.active}
        onToggleFullscreen={() => void fullscreen.toggle()}
        onExit={publicView ? undefined : () => navigate(`/app/funnels/${funnelId}`)}
      />
      <div className="relative flex min-h-0 flex-1">
        <PresentationCanvas
          nodes={graph.nodes}
          edges={graph.edges}
          emphasis={emphasis}
          defaultViewport={config.defaultViewport}
          focusNodeIds={
            !focusInteracted
              ? undefined
              : activeHighlight
                ? activeHighlight.nodeIds
                : stageFocusCategory
                  ? [...emphasis.nodes.entries()]
                      .filter(([, state]) => state === 'full')
                      .map(([nodeId]) => nodeId)
                  : null
          }
          strongEmphasis={activeHighlight?.emphasis === 'strong'}
          onNodeClick={(nodeId) => config.showMetrics && setSelectedNodeId(nodeId)}
          onPaneClick={() => setSelectedNodeId(null)}
        />
        {selectedNodeId && metricsResult ? (
          <PresentationMetricsPanel
            nodeName={
              (graph.nodes.find((n) => n.id === selectedNodeId)?.data as { name?: string })?.name ??
              selectedNodeId
            }
            result={metricsResult.propagation.nodeResults.get(selectedNodeId)}
            onClose={() => setSelectedNodeId(null)}
          />
        ) : null}
        {!publicView ? (
          <StrategicNotesPanel
            notes={config.strategicNotes}
            open={notesOpen}
            onClose={() => setNotesOpen(false)}
          />
        ) : null}
      </div>
    </div>
  );
}
